/**
 * Reserved organisation slugs.
 *
 * When tenant identity moves from URL path segment to DNS label (see epic #149
 * / work item #716), each `organizations.slug` value becomes a live subdomain
 * such as `penny.solution.co.uk`. That means a slug can now collide with an
 * infrastructure hostname (e.g. `www`, `api`, `dev`), silently break the
 * environment, or bypass the tenant-scoped auth boundary.
 *
 * This module owns the single source of truth for slugs that must NEVER be
 * assigned to an organisation. It is imported by:
 *   - the middleware rewrite layer (to reject reserved hosts as 404 rather
 *     than resolving them as tenants);
 *   - the admin org create / edit UI (to surface a validation error before
 *     the row is written); and
 *   - the safety migration that adds a CHECK constraint on the column.
 *
 * The list is intentionally broad. Adding an entry is cheap; removing one
 * after a customer has been onboarded onto that slug is not.
 */

/**
 * DNS labels reserved across every environment.
 *
 * Grouped by category to make future edits easier to review. Keep entries
 * lowercase (DNS labels are case-insensitive but our validator normalises to
 * lowercase) and free of hyphens/underscores at the boundaries.
 */
export const RESERVED_SUBDOMAINS: ReadonlySet<string> = new Set<string>([
  // Environment labels — these ARE the base host in dev/uat/local and must
  // never be shadowed by a tenant slug.
  'dev',
  'uat',
  'staging',
  'stage',
  'prod',
  'production',
  'test',
  'local',
  'localhost',

  // Public / marketing infrastructure.
  'www',
  'app',
  'apps',
  'web',
  'site',
  'blog',
  'docs',
  'help',
  'support',
  'status',
  'kb',

  // API / service endpoints.
  'api',
  'apis',
  'graphql',
  'grpc',
  'ws',
  'sockets',

  // Auth / admin surfaces.
  'admin',
  'auth',
  'sso',
  'oauth',
  'oidc',
  'saml',
  'login',
  'logout',
  'signup',
  'signin',
  'account',
  'accounts',
  'billing',
  'checkout',

  // Data / observability (Metabase already lives at data.solution.co.uk).
  'data',
  'analytics',
  'metrics',
  'monitoring',
  'logs',
  'grafana',
  'kibana',
  'prometheus',
  'sentry',
  'metabase',

  // Assets / CDN / storage.
  'cdn',
  'static',
  'assets',
  'files',
  'media',
  'img',
  'images',
  'storage',
  'download',
  'downloads',

  // Mail / comms.
  'mail',
  'smtp',
  'imap',
  'inbucket',
  'email',
  'newsletter',

  // Internal / vendor tooling.
  'vercel',
  'supabase',
  'embark',
  'iovation',
  'transunion',
  'gitlab',
  'internal',
  'ops',
  'sre',
  'platform',
  'security',
  'legal',
  'privacy',
  'terms',
  'compliance',

  // Referral / session paths — reserved to keep the option open of promoting
  // them to first-class subdomains later without a data migration.
  'r',
  'session',
  'sessions',
  'shared',
  'preview',
  'demo',
]);

/**
 * Returns `true` when the given DNS label must not be used as an organisation
 * slug. The comparison is case-insensitive; callers do not need to normalise.
 */
export function isReservedSubdomain(label: string): boolean {
  if (!label) return false;
  return RESERVED_SUBDOMAINS.has(label.toLowerCase());
}

/**
 * DNS label shape check per RFC 1035 §2.3.1: 1–63 chars, alphanumerics and
 * hyphens only, no leading or trailing hyphen. Enforced ALONGSIDE the
 * reserved-word check by both the middleware and the admin UI.
 */
const DNS_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Returns `true` when the label is a valid DNS label AND not on the reserved
 * list. Use this as the single admission gate for new organisation slugs.
 */
export function isAllowedOrgSlug(label: string): boolean {
  if (typeof label !== 'string') return false;
  const normalised = label.toLowerCase();
  if (!DNS_LABEL.test(normalised)) return false;
  if (isReservedSubdomain(normalised)) return false;
  return true;
}
